// Candlestick pattern detector — standard public-domain definitions (doji, hammer,
// engulfing, stars, soldiers…). Reversal patterns are qualified by the prior trend so a
// hammer only fires after a decline, a shooting star only after a rise. Bars oldest→newest.
// Pairs with the gauges cluster on the Charts tab; same Bar shape.
import type { Bar } from './gauges';

export type Dir = 'bull' | 'bear' | 'neutral';
export interface Pattern { name: string; dir: Dir; note: string; }

// ── candle anatomy ──────────────────────────────────────────────────────────
const body  = (b: Bar) => Math.abs(b.c - b.o);
const range = (b: Bar) => (b.h - b.l) || 1e-9;
const upper = (b: Bar) => b.h - Math.max(b.o, b.c);
const lower = (b: Bar) => Math.min(b.o, b.c) - b.l;
const bull  = (b: Bar) => b.c > b.o;
const bear  = (b: Bar) => b.c < b.o;
const bTop  = (b: Bar) => Math.max(b.o, b.c);
const bBot  = (b: Bar) => Math.min(b.o, b.c);
const mid   = (b: Bar) => (b.o + b.c) / 2;

// Trend leading INTO candle i (exclusive): % change over the prior `n` closes.
function trendInto(bars: Bar[], i: number, n = 5): Dir {
  if (i - n < 0) return 'neutral';
  const a = bars[i - n].c, z = bars[i - 1].c;
  const chg = (z - a) / a;
  return chg > 0.015 ? 'bull' : chg < -0.015 ? 'bear' : 'neutral';
}

// ── detection at index i (needs i-2 for triples) ─────────────────────────────
export function patternsAt(bars: Bar[], i: number): Pattern[] {
  const out: Pattern[] = [];
  if (i < 0 || i >= bars.length) return out;
  const c = bars[i];
  const t = trendInto(bars, i);
  const R = range(c), bd = body(c), up = upper(c), lo = lower(c);

  // ── single-candle ──────────────────────────────────────────────────────
  const doji = bd <= 0.1 * R;
  if (doji && lo >= 0.6 * R && up <= 0.1 * R)
    out.push({ name: 'Dragonfly Doji', dir: 'bull', note: 'rejection of lows · indecision→up' });
  else if (doji && up >= 0.6 * R && lo <= 0.1 * R)
    out.push({ name: 'Gravestone Doji', dir: 'bear', note: 'rejection of highs · indecision→down' });
  else if (doji)
    out.push({ name: 'Doji', dir: 'neutral', note: 'open≈close · indecision, watch next bar' });

  if (!doji && up <= 0.05 * R && lo <= 0.05 * R && bd >= 0.9 * R)
    out.push({ name: 'Marubozu', dir: bull(c) ? 'bull' : 'bear', note: 'full-body conviction candle' });

  // hammer / hanging man (long lower wick, body up top)
  if (!doji && lo >= 2 * bd && up <= 0.25 * R && bd <= 0.35 * R) {
    if (t === 'bear') out.push({ name: 'Hammer', dir: 'bull', note: 'long lower wick after decline · needs confirm' });
    else if (t === 'bull') out.push({ name: 'Hanging Man', dir: 'bear', note: 'long lower wick after rally · needs confirm' });
  }
  // inverted hammer / shooting star (long upper wick, body down low)
  if (!doji && up >= 2 * bd && lo <= 0.25 * R && bd <= 0.35 * R) {
    if (t === 'bear') out.push({ name: 'Inverted Hammer', dir: 'bull', note: 'long upper wick after decline · needs confirm' });
    else if (t === 'bull') out.push({ name: 'Shooting Star', dir: 'bear', note: 'long upper wick after rally · needs confirm' });
  }
  // spinning top (small body, both wicks)
  if (!doji && bd <= 0.3 * R && up >= 0.25 * R && lo >= 0.25 * R)
    out.push({ name: 'Spinning Top', dir: 'neutral', note: 'small body, two wicks · indecision' });

  // ── two-candle ─────────────────────────────────────────────────────────
  if (i >= 1) {
    const p = bars[i - 1];
    if (bear(p) && bull(c) && c.c >= p.o && c.o <= p.c && body(c) > body(p) && t !== 'bull')
      out.push({ name: 'Bullish Engulfing', dir: 'bull', note: 'green body swallows prior red' });
    if (bull(p) && bear(c) && c.o >= p.c && c.c <= p.o && body(c) > body(p) && t !== 'bear')
      out.push({ name: 'Bearish Engulfing', dir: 'bear', note: 'red body swallows prior green' });
    if (bear(p) && body(p) > 0.5 * range(p) && bTop(c) <= bTop(p) && bBot(c) >= bBot(p)
        && body(c) < 0.6 * body(p) && t === 'bear')
      out.push({ name: 'Bullish Harami', dir: 'bull', note: 'inside bar after decline · momentum stalling' });
    if (bull(p) && body(p) > 0.5 * range(p) && bTop(c) <= bTop(p) && bBot(c) >= bBot(p)
        && body(c) < 0.6 * body(p) && t === 'bull')
      out.push({ name: 'Bearish Harami', dir: 'bear', note: 'inside bar after rally · momentum stalling' });
    if (bear(p) && bull(c) && c.o < p.l && c.c > mid(p) && c.c < p.o && t === 'bear')
      out.push({ name: 'Piercing Line', dir: 'bull', note: 'closes back above prior midpoint' });
    if (bull(p) && bear(c) && c.o > p.h && c.c < mid(p) && c.c > p.o && t === 'bull')
      out.push({ name: 'Dark Cloud Cover', dir: 'bear', note: 'closes back below prior midpoint' });
    const eqLo = Math.abs(p.l - c.l) <= 0.1 * R, eqHi = Math.abs(p.h - c.h) <= 0.1 * R;
    if (eqLo && t === 'bear' && bear(p) && bull(c))
      out.push({ name: 'Tweezer Bottom', dir: 'bull', note: 'matched lows reject support' });
    if (eqHi && t === 'bull' && bull(p) && bear(c))
      out.push({ name: 'Tweezer Top', dir: 'bear', note: 'matched highs reject resistance' });
  }

  // ── three-candle ───────────────────────────────────────────────────────
  if (i >= 2) {
    const a = bars[i - 2], b = bars[i - 1];
    const ta = trendInto(bars, i - 2);
    const smallMid = body(b) < 0.4 * body(a);
    if (bear(a) && smallMid && bull(c) && c.c > mid(a) && ta !== 'bull')
      out.push({ name: 'Morning Star', dir: 'bull', note: 'down → indecision → up · 3-bar reversal' });
    if (bull(a) && smallMid && bear(c) && c.c < mid(a) && ta !== 'bear')
      out.push({ name: 'Evening Star', dir: 'bear', note: 'up → indecision → down · 3-bar reversal' });
    if (bull(a) && bull(b) && bull(c) && b.c > a.c && c.c > b.c
        && b.o > a.o && b.o < a.c && c.o > b.o && c.o < b.c && upper(b) < 0.3 * range(b))
      out.push({ name: 'Three White Soldiers', dir: 'bull', note: 'three rising green closes · trend thrust' });
    if (bear(a) && bear(b) && bear(c) && b.c < a.c && c.c < b.c
        && b.o < a.o && b.o > a.c && c.o < b.o && c.o > b.c && lower(b) < 0.3 * range(b))
      out.push({ name: 'Three Black Crows', dir: 'bear', note: 'three falling red closes · trend thrust' });
  }
  return out;
}

// Scan the latest candle (most recent N for resilience to a quiet last bar).
export function detectPatterns(bars: Bar[], lookback = 1): Pattern[] {
  const seen = new Set<string>(), out: Pattern[] = [];
  for (let k = 0; k < lookback; k++) {
    const i = bars.length - 1 - k;
    for (const p of patternsAt(bars, i)) if (!seen.has(p.name)) { seen.add(p.name); out.push(p); }
  }
  return out;
}

// ── HTML readout (gauges-style chips) ────────────────────────────────────────
const COLOR: Record<Dir, string> = { bull: '#26a69a', bear: '#ef5350', neutral: '#9aa0a6' };
const GLYPH: Record<Dir, string> = { bull: '▲', bear: '▼', neutral: '◆' };

export function buildPatterns(bars: Bar[], tfLabel: string): string {
  if (bars.length < 3)
    return `<span style="font-size:10px;color:var(--text3);">warming up · ${bars.length}/3 bars</span>`;
  const ps = detectPatterns(bars);
  if (!ps.length)
    return `<span style="font-size:11px;color:var(--text3);font-family:var(--mono);">no notable pattern on last ${tfLabel} candle</span>`;
  const chips = ps.map(p => {
    const col = COLOR[p.dir];
    return `<span title="${p.note}" style="display:inline-flex;align-items:center;gap:5px;`
      + `font-size:11px;font-family:var(--mono);border:1px solid ${col}55;color:${col};`
      + `border-radius:3px;padding:3px 9px;background:${col}11;">`
      + `<span style="font-size:11px;">${GLYPH[p.dir]}</span>${p.name}</span>`;
  }).join('');
  return `<div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center;">${chips}</div>`;
}
